/**
 * Desktop sidebar navigation with expandable/collapsible states (72px collapsed, 240px expanded).
 * Pill-style active indicators, smooth transitions. Auto-hides on mobile.
 * @remarks Accessible: NavLink focus, expand/collapse button with aria-label
 * @example
 * ```tsx
 * <Navigation
 *   items={[{id: 'overview', label: 'Overview', href: '/', icon: <Home />}]}
 *   isExpanded={expanded}
 *   onToggle={toggleSidebar}
 * />
 * ```
 */

import { forwardRef, type CSSProperties } from "react";
import { useLocation, NavLink } from "react-router-dom";
import { spacingNum, radius, transition, easing } from "../tokens";
import { useTheme } from "../index";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

function createDynamicStyles(styles: CSSProperties): CSSProperties {
  return styles;
}

const transitions = {
  fast: transition.fast,
  normal: transition.normal,
  easeStandard: easing.standard,
};

export interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
}

export interface NavigationProps {
  items: NavItem[];
  isExpanded?: boolean;
  onToggle?: () => void;
}

export const Navigation = forwardRef<HTMLElement, NavigationProps>(
  ({ items, isExpanded = false, onToggle }, ref) => {
    const location = useLocation();
    const { brand, surface, text: textColors, isMobile } = useTheme();

    if (isMobile) return null;

    const sidebarStyles = createDynamicStyles({
      position: "fixed",
      left: 0,
      top: 0,
      height: "100vh",
      width: isExpanded ? "240px" : "72px",
      backgroundColor: surface.surface,
      borderRight: `1px solid ${surface.border}`,
      padding: `${spacingNum[6]}px ${spacingNum[3]}px`,
      display: "flex",
      flexDirection: "column",
      gap: `${spacingNum[4]}px`,
      transition: `width 300ms ${transitions.easeStandard}`,
      // zIndex: zIndex.navigation,
      overflow: "hidden",
      boxShadow: "var(--shadow-neumorphic-md)",
    });

    const toggleButtonStyles = createDynamicStyles({
      width: "100%",
      height: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "transparent",
      border: `1px solid ${surface.border}`,
      borderRadius: radius.md,
      color: textColors.secondary,
      cursor: "pointer",
      transition: `all 0.2s ease`,
      marginBottom: `${spacingNum[4]}px`,
      WebkitTapHighlightColor: "transparent",
      touchAction: "manipulation",
    });

    const navItemStyles = (isActive: boolean) =>
      createDynamicStyles({
        display: "flex",
        alignItems: "center",
        gap: `${spacingNum[3]}px`,
        padding: `${spacingNum[3]}px ${spacingNum[4]}px`,
        minHeight: "44px",
        borderRadius: radius.md,
        backgroundColor: isActive ? brand.primary : "transparent",
        color: isActive ? textColors.inverse : textColors.primary,
        textDecoration: "none",
        fontSize: "15px",
        fontWeight: isActive ? 600 : 500,
        transition: `all 0.2s ease`,
        cursor: "pointer",
        border: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
      });

    const iconStyles = createDynamicStyles({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      width: "20px",
      height: "20px",
    });

    return (
      <nav ref={ref} style={sidebarStyles} aria-label="Main navigation">
        {/* Toggle button */}
        <button
          type="button"
          onClick={onToggle}
          style={toggleButtonStyles}
          aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          aria-expanded={isExpanded}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = surface.hover;
            e.currentTarget.style.borderColor = brand.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.borderColor = surface.border;
          }}
        >
          {isExpanded ? (
            <PanelLeftClose
              size={20}
              aria-hidden="true"
              color={textColors.primary}
            />
          ) : (
            <PanelLeftOpen
              size={20}
              aria-hidden="true"
              color={textColors.primary}
            />
          )}
        </button>

        {/* Nav items */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: `${spacingNum[2]}px`,
          }}
        >
          {items.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.id}
                to={item.href}
                style={({ isActive }) => navItemStyles(isActive)}
                aria-current={isActive ? "page" : undefined}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = surface.hover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <span style={iconStyles} aria-hidden="true">
                  {item.icon}
                </span>
                {isExpanded && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </div>
      </nav>
    );
  }
);

Navigation.displayName = "Navigation";
